import { useMemo, useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Settings2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (value) => {
  if (!value || isNaN(value)) return "₹0";
  if (Math.abs(value) >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (Math.abs(value) >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return `₹${Math.round(value).toLocaleString("en-IN")}`;
};

// ============ TEMPLATE MANAGER DIALOG ============
const TemplateManagerDialog = ({ open, onOpenChange, templates, onRefresh }) => {
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [name, setName] = useState("");
  const [buildingHeight, setBuildingHeight] = useState("");
  const [totalMonths, setTotalMonths] = useState(36);
  const [quarters, setQuarters] = useState(() =>
    Array.from({ length: 12 }, (_, i) => ({
      quarter: i + 1,
      label: `Quarter ${i + 1}`,
      cost_percent: parseFloat((100 / 12).toFixed(1)),
    }))
  );
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setEditingTemplate(null);
    setName("");
    setBuildingHeight("");
    setTotalMonths(36);
    const numQ = Math.ceil(36 / 3);
    setQuarters(Array.from({ length: numQ }, (_, i) => ({
      quarter: i + 1,
      label: `Quarter ${i + 1}`,
      cost_percent: parseFloat((100 / numQ).toFixed(1)),
    })));
  };

  const loadTemplate = (t) => {
    setEditingTemplate(t);
    setName(t.name);
    setBuildingHeight(t.building_height);
    setTotalMonths(t.total_months);
    setQuarters(t.quarters.map(q => ({ ...q })));
  };

  const handleMonthsChange = (months) => {
    const m = parseInt(months) || 12;
    setTotalMonths(m);
    const numQ = Math.ceil(m / 3);
    const existing = [...quarters];
    if (numQ > existing.length) {
      for (let i = existing.length; i < numQ; i++) {
        existing.push({ quarter: i + 1, label: `Quarter ${i + 1}`, cost_percent: 0 });
      }
    } else if (numQ < existing.length) {
      existing.length = numQ;
    }
    // Renumber
    existing.forEach((q, i) => { q.quarter = i + 1; });
    setQuarters(existing);
  };

  const updateQuarterCost = (idx, val) => {
    const updated = [...quarters];
    updated[idx] = { ...updated[idx], cost_percent: parseFloat(val) || 0 };
    setQuarters(updated);
  };

  const updateQuarterLabel = (idx, val) => {
    const updated = [...quarters];
    updated[idx] = { ...updated[idx], label: val };
    setQuarters(updated);
  };

  const totalPercent = quarters.reduce((s, q) => s + q.cost_percent, 0);

  const normalizeToHundred = () => {
    if (totalPercent === 0) return;
    const factor = 100 / totalPercent;
    setQuarters(quarters.map(q => ({
      ...q,
      cost_percent: parseFloat((q.cost_percent * factor).toFixed(1)),
    })));
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Template name required"); return; }
    setSaving(true);
    try {
      const payload = { name, building_height: buildingHeight, total_months: totalMonths, quarters };
      if (editingTemplate) {
        await axios.put(`${API}/cashflow-templates/${editingTemplate.id}`, payload);
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/cashflow-templates`, payload);
        toast.success("Template created");
      }
      onRefresh();
      resetForm();
    } catch (err) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/cashflow-templates/${id}`);
      toast.success("Template deleted");
      onRefresh();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cash Flow Templates</DialogTitle>
          <DialogDescription>Create and manage quarter-wise cost distribution templates</DialogDescription>
        </DialogHeader>

        {/* Existing templates list */}
        <div className="space-y-2 mb-4">
          <p className="text-sm font-medium">Saved Templates</p>
          {templates.map(t => (
            <div key={t.id} className="flex items-center justify-between p-2 border rounded-sm text-sm">
              <div>
                <span className="font-medium">{t.name}</span>
                <span className="text-muted-foreground ml-2">({t.total_months}mo / {t.quarters.length}Q)</span>
                {t.is_default && <span className="ml-2 text-xs bg-secondary px-1.5 py-0.5 rounded">Default</span>}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => loadTemplate(t)} data-testid={`edit-template-${t.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                {!t.is_default && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(t.id)} data-testid={`delete-template-${t.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <Separator />

        {/* Create / Edit form */}
        <div className="space-y-4 pt-2">
          <p className="text-sm font-medium">{editingTemplate ? `Editing: ${editingTemplate.name}` : "Create New Template"}</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Template Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. G+14 Custom" className="rounded-sm h-8 text-sm" data-testid="template-name-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Building Height (m)</Label>
              <Input value={buildingHeight} onChange={(e) => setBuildingHeight(e.target.value)} placeholder="e.g. 45" className="rounded-sm h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Total Months</Label>
              <Input type="number" value={totalMonths} onChange={(e) => handleMonthsChange(e.target.value)} className="rounded-sm h-8 text-sm" data-testid="template-months-input" />
            </div>
          </div>

          {/* Quarter-wise cost inputs */}
          <div className="border rounded-sm overflow-hidden">
            <div className="grid grid-cols-12 gap-0 bg-secondary/50 text-xs font-medium p-2">
              <div className="col-span-1">Q#</div>
              <div className="col-span-7">Phase Label</div>
              <div className="col-span-4 text-right">Cost %</div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {quarters.map((q, i) => (
                <div key={`quarter-${q.quarter}`} className="grid grid-cols-12 gap-0 items-center p-1 border-t text-sm">
                  <div className="col-span-1 text-center text-xs text-muted-foreground">Q{q.quarter}</div>
                  <div className="col-span-7 px-1">
                    <Input value={q.label} onChange={(e) => updateQuarterLabel(i, e.target.value)} className="rounded-sm h-7 text-xs" />
                  </div>
                  <div className="col-span-4 px-1">
                    <Input type="number" step="0.1" value={q.cost_percent} onChange={(e) => updateQuarterCost(i, e.target.value)} className="rounded-sm h-7 text-xs text-right" />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center p-2 border-t bg-secondary/30">
              <span className={`text-xs font-medium ${Math.abs(totalPercent - 100) < 0.5 ? "text-green-700" : "text-destructive"}`}>
                Total: {totalPercent.toFixed(1)}%
              </span>
              {Math.abs(totalPercent - 100) >= 0.5 && (
                <Button variant="outline" size="sm" className="h-6 text-xs" onClick={normalizeToHundred} data-testid="normalize-btn">
                  Normalize to 100%
                </Button>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {editingTemplate && (
            <Button variant="ghost" size="sm" onClick={resetForm}>Cancel Edit</Button>
          )}
          <DialogClose asChild><Button variant="outline" size="sm">Close</Button></DialogClose>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-template-btn">
            {editingTemplate ? "Update Template" : "Create Template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ MAIN CASH FLOW ANALYSIS ============
export const CashFlowAnalysis = ({ plData, formData, setFormData }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  // Quarter-wise editable cost distribution (loaded from template, user-editable)
  const [costQuarters, setCostQuarters] = useState([]);
  // Quarter-wise sales plan (with per-quarter rate)
  const [salesQuarters, setSalesQuarters] = useState([]);
  const [initialized, setInitialized] = useState(false);

  // RERA collection stages (editable)
  const [reraStages, setReraStages] = useState([
    { name: "Booking", percent: 10, quarter: 1 },
    { name: "Plinth", percent: 35, quarter: 3 },
    { name: "1st Slab RCC", percent: 15, quarter: 5 },
    { name: "RCC Complete", percent: 15, quarter: 7 },
    { name: "Brickwork / Plaster", percent: 10, quarter: 9 },
    { name: "Flooring / Finishing", percent: 10, quarter: 10 },
    { name: "OC / Possession", percent: 5, quarter: 12 },
  ]);
  const [showReraEditor, setShowReraEditor] = useState(false);

  // Reset when a different project is loaded
  const projectKey = formData?.projectName || formData?.societyName || "";
  useEffect(() => {
    setInitialized(false);
  }, [projectKey]);

  const [showCostEditor, setShowCostEditor] = useState(false);
  const [showCostRules, setShowCostRules] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/cashflow-templates`);
      setTemplates(resp.data);
    } catch (err) {
      console.error("Failed to load templates", err);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Restore saved config from formData OR auto-select template
  useEffect(() => {
    if (templates.length === 0 || initialized) return;
    const saved = formData?.cashflowConfig;
    if (saved && saved.costQuarters?.length > 0) {
      setSelectedTemplateId(saved.selectedTemplateId || "");
      setCostQuarters(saved.costQuarters);
      setSalesQuarters(saved.salesQuarters || []);
      if (saved.reraStages?.length > 0) setReraStages(saved.reraStages);
      setInitialized(true);
      return;
    }
    // Auto-select template based on building height
    const bh = parseInt(formData?.buildingHeight) || 24;
    let best = templates[0];
    for (const t of templates) {
      const th = parseInt(t.building_height) || 0;
      if (th <= bh) best = t;
    }
    applyTemplate(best);
    setInitialized(true);
  }, [templates, formData?.buildingHeight, formData?.cashflowConfig]);

  // Persist changes back to formData for saving
  useEffect(() => {
    if (!initialized || costQuarters.length === 0) return;
    const config = { selectedTemplateId, costQuarters, salesQuarters, reraStages };
    // Only update if actually different to avoid infinite loops
    const prev = formData?.cashflowConfig;
    if (prev && JSON.stringify(prev) === JSON.stringify(config)) return;
    setFormData(fd => ({ ...fd, cashflowConfig: config }));
  }, [selectedTemplateId, costQuarters, salesQuarters, reraStages, initialized]);

  const applyTemplate = (tmpl) => {
    if (!tmpl) return;
    setSelectedTemplateId(tmpl.id);
    setCostQuarters(tmpl.quarters.map(q => ({ ...q })));
    // Initialize sales quarters matching the template length
    const numQ = tmpl.quarters.length;
    const marketRate = Math.round(plData?.saleRatePerSqft || 0);
    const dpRate = Math.round(marketRate * 0.65 / 100) * 100; // 35% lower, rounded to nearest 100
    const balancePercent = numQ > 1 ? parseFloat((90 / (numQ - 1)).toFixed(1)) : 0;
    setSalesQuarters(prev => {
      if (prev.length === numQ) return prev;
      return Array.from({ length: numQ }, (_, i) => ({
        quarter: i + 1,
        sale_percent: i === 0 ? 10 : balancePercent,
        sale_rate: i === 0 ? dpRate : marketRate,
      }));
    });
  };

  const handleTemplateChange = (tmplId) => {
    const tmpl = templates.find(t => t.id === tmplId);
    if (tmpl) applyTemplate(tmpl);
  };

  // Cost quarter editing
  const updateCostPercent = (idx, val) => {
    const updated = [...costQuarters];
    updated[idx] = { ...updated[idx], cost_percent: parseFloat(val) || 0 };
    setCostQuarters(updated);
  };

  const costTotal = costQuarters.reduce((s, q) => s + q.cost_percent, 0);

  const normalizeCosts = () => {
    if (costTotal === 0) return;
    const factor = 100 / costTotal;
    setCostQuarters(costQuarters.map(q => ({
      ...q,
      cost_percent: parseFloat((q.cost_percent * factor).toFixed(1)),
    })));
  };

  // Sales quarter editing
  const updateSalePercent = (idx, val) => {
    const updated = [...salesQuarters];
    updated[idx] = { ...updated[idx], sale_percent: parseFloat(val) || 0 };
    setSalesQuarters(updated);
  };

  const updateSaleRate = (idx, val) => {
    const updated = [...salesQuarters];
    updated[idx] = { ...updated[idx], sale_rate: parseFloat(val) || 0 };
    setSalesQuarters(updated);
  };

  const salesTotal = salesQuarters.reduce((s, q) => s + q.sale_percent, 0);

  const normalizeSales = () => {
    if (salesTotal === 0) return;
    const factor = 100 / salesTotal;
    setSalesQuarters(salesQuarters.map(q => ({
      ...q,
      sale_percent: parseFloat((q.sale_percent * factor).toFixed(1)),
    })));
  };

  // Add/remove quarter
  const addQuarter = () => {
    const n = costQuarters.length + 1;
    const defaultRate = Math.round(plData?.saleRatePerSqft || 0);
    setCostQuarters([...costQuarters, { quarter: n, label: `Quarter ${n}`, cost_percent: 0 }]);
    setSalesQuarters([...salesQuarters, { quarter: n, sale_percent: 0, sale_rate: defaultRate }]);
  };

  const removeLastQuarter = () => {
    if (costQuarters.length <= 2) return;
    setCostQuarters(costQuarters.slice(0, -1));
    setSalesQuarters(salesQuarters.slice(0, -1));
  };

  // RERA stage editing
  const updateReraStage = (idx, field, val) => {
    const updated = [...reraStages];
    updated[idx] = { ...updated[idx], [field]: field === "name" ? val : (parseFloat(val) || 0) };
    setReraStages(updated);
  };
  const addReraStage = () => {
    const maxQ = costQuarters.length || 12;
    setReraStages([...reraStages, { name: `Stage ${reraStages.length + 1}`, percent: 0, quarter: maxQ }]);
  };
  const removeReraStage = (idx) => {
    setReraStages(reraStages.filter((_, i) => i !== idx));
  };
  const reraTotal = reraStages.reduce((s, st) => s + st.percent, 0);
  const normalizeRera = () => {
    if (reraTotal === 0) return;
    const f = 100 / reraTotal;
    setReraStages(reraStages.map(s => ({ ...s, percent: parseFloat((s.percent * f).toFixed(1)) })));
  };

  // ANALYSIS CALCULATION
  const analysis = useMemo(() => {
    if (!plData || costQuarters.length === 0) return null;

    const totalExpenses = plData.totalExpenses || 0;
    const totalSaleableArea = plData.finalSaleAreaSqft || 0;
    const marketRate = plData.saleRatePerSqft || 0;
    const initialInvestment = plData.initialInvestment || 0;
    const regularRevenue = plData.totalRevenue || 0;
    const regularProfit = plData.grossProfit || 0;

    if (totalSaleableArea <= 0 || marketRate <= 0) return null;

    const numQ = costQuarters.length;
    const lastQ = numQ - 1;

    // ===== ITEMIZED COST HEADS =====
    const rent = plData.totalRentalCost || 0;
    const construction = plData.totalConstructionCost || 0;
    const bankInterest = plData.totalBankInterest || 0;
    const pmcCost = plData.pmcCost || 0;
    const otherCosts = plData.totalOtherCosts || 0; // marketing + misc + brokerage
    const corpus = plData.corpusCharges || 0;
    const betterment = plData.bettermentCharges || 0;
    const shifting = plData.shiftingCharges || 0;

    // ===== DISTRIBUTION RULES =====
    // Q1: Initial Investment (TDR, Paid FSI 20%, Sanction, DA, BG, deposit, brokerage, 50% shifting, betterment if split)
    //     + Construction (Q1 %) + Other proportional costs (Q1 %)
    // Q2 to last-1: Rent + Construction (Qi %) + Other proportional costs (Qi %)
    // Last Q: Rent + Construction + Other proportional + Corpus + Betterment + 50% Shifting
    
    const rentPerQ = numQ > 0 ? rent / numQ : 0;
    
    // Costs distributed by template percentage: construction + other + bank interest + PMC
    const proportionalCosts = construction + otherCosts + bankInterest + pmcCost;
    
    // End-of-project costs (last quarter)
    const endOfProjectCosts = corpus + betterment + (shifting * 0.5);

    // Build collection array: how much cash arrives each quarter
    const collections = new Array(numQ).fill(0);
    let totalAltRevenue = 0;

    // For each quarter's sales, calculate total revenue and when it's collected
    salesQuarters.forEach((sq, i) => {
      if (sq.sale_percent <= 0) return;
      const areaSold = totalSaleableArea * (sq.sale_percent / 100);
      const rate = sq.sale_rate || marketRate;
      const saleRevenue = areaSold * rate;
      totalAltRevenue += saleRevenue;

      if (i === 0) {
        // Q1: Down Payment — 100% collected immediately
        collections[0] += saleRevenue;
      } else {
        // Q2+: RERA stage-wise collection
        reraStages.forEach(stage => {
          const collectQ = Math.min(stage.quarter - 1, numQ - 1);
          if (collectQ >= i) {
            collections[collectQ] += saleRevenue * (stage.percent / 100);
          } else {
            collections[i] += saleRevenue * (stage.percent / 100);
          }
        });
      }
    });

    // Build rows with itemized outflows and collections
    let cumInflow = 0, cumOutflow = 0;
    const rows = costQuarters.map((cq, i) => {
      const sq = salesQuarters[i] || { sale_percent: 0, sale_rate: marketRate };
      const costPct = cq.cost_percent / 100;

      // Itemized outflows
      const rentOut = rentPerQ;
      const constructionOut = construction * costPct;
      const otherProportionalOut = (otherCosts + bankInterest + pmcCost) * costPct;
      
      // Q1: Initial Investment + construction + other proportional + rent
      // Last Q: construction + other proportional + rent + corpus + betterment + 50% shifting
      // Middle Qs: construction + other proportional + rent
      const initialInvestmentOut = i === 0 ? initialInvestment : 0;
      const endProjectOut = i === lastQ ? endOfProjectCosts : 0;

      const outflow = initialInvestmentOut + constructionOut + otherProportionalOut + rentOut + endProjectOut;
      const inflow = collections[i];
      const areaSold = totalSaleableArea * (sq.sale_percent / 100);
      const saleRevenue = areaSold * (sq.sale_rate || marketRate);

      cumInflow += inflow;
      cumOutflow += outflow;

      return {
        quarter: cq.quarter,
        label: cq.label,
        costPercent: cq.cost_percent,
        salePercent: sq.sale_percent,
        areaSold,
        rate: sq.sale_rate || marketRate,
        saleRevenue,
        isDownPayment: i === 0,
        inflow,
        outflow,
        cumInflow,
        cumOutflow,
        netPosition: cumInflow - cumOutflow,
        // Breakdown for display
        breakdown: {
          initialInvestment: initialInvestmentOut,
          rent: rentOut,
          construction: constructionOut,
          otherProportional: otherProportionalOut,
          endOfProject: endProjectOut,
        },
      };
    });

    const altProfit = totalAltRevenue - totalExpenses;
    const revenueLoss = regularRevenue - totalAltRevenue;

    return {
      rows,
      totalExpenses,
      totalSaleableArea,
      marketRate,
      initialInvestment,
      regularRevenue,
      regularProfit,
      totalAltRevenue,
      altProfit,
      revenueLoss,
      salesTotal,
      costTotal,
      // Cost head totals for display
      costHeads: { rent, construction, otherCosts, bankInterest, pmcCost, corpus, betterment, shifting, initialInvestment, endOfProjectCosts, proportionalCosts },
    };
  }, [plData, costQuarters, salesQuarters, reraStages, salesTotal, costTotal]);

  if (!plData) {
    return (
      <Card className="rounded-sm shadow-sm bg-secondary/50">
        <CardContent className="py-8 text-center text-muted-foreground" data-testid="cashflow-no-data">
          <p>Complete Quick Input and FSI calculations first to use Cash Flow Analysis</p>
        </CardContent>
      </Card>
    );
  }

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  return (
    <div className="space-y-6">
      {/* Header + Template Selection */}
      <Card className="rounded-sm shadow-sm border-blue-200 bg-blue-50/30" data-testid="cashflow-header">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="font-heading tracking-tight text-blue-900">
                Alternative Cash Flow Analysis
              </CardTitle>
              <CardDescription>
                Quarter-wise cost distribution and sales plan. Select a template based on building height, then customize.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => setTemplateDialogOpen(true)}
              data-testid="manage-templates-btn"
            >
              <Settings2 className="h-4 w-4 mr-1" />
              Manage Templates
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Cost Distribution Template</Label>
              <Select value={selectedTemplateId} onValueChange={handleTemplateChange} data-testid="template-selector">
                <SelectTrigger className="rounded-sm h-9">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} ({t.quarters.length}Q)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Market Rate (from Quick Input)</Label>
              <div className="h-9 flex items-center px-3 bg-secondary/50 rounded-sm text-sm font-mono">
                ₹{Math.round(plData.saleRatePerSqft || 0).toLocaleString("en-IN")}/sft
              </div>
              <p className="text-[10px] text-muted-foreground">
                Adjust individual quarter rates in the Sales Plan below
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quarter-wise Cost Distribution (collapsible editor) */}
      <Card className="rounded-sm shadow-sm" data-testid="cost-distribution-card">
        <CardHeader className="cursor-pointer" onClick={() => setShowCostEditor(!showCostEditor)}>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-heading tracking-tight">
                Quarter-wise Cost Distribution
                {selectedTemplate && <span className="text-sm font-normal text-muted-foreground ml-2">({selectedTemplate.name})</span>}
              </CardTitle>
              <CardDescription>
                Total: <span className={Math.abs(costTotal - 100) < 0.5 ? "text-green-700 font-semibold" : "text-destructive font-semibold"}>{costTotal.toFixed(1)}%</span>
                {' '} | {costQuarters.length} quarters
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {Math.abs(costTotal - 100) >= 0.5 && (
                <Button variant="outline" size="sm" className="h-7 text-xs" type="button" onClick={(e) => { e.stopPropagation(); normalizeCosts(); }} data-testid="normalize-costs-btn">
                  Normalize to 100%
                </Button>
              )}
              {showCostEditor ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
          </div>
        </CardHeader>
        {showCostEditor && (
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {costQuarters.map((q, i) => (
                <div key={`cost-q${q.quarter}`} className="p-2 border rounded-sm space-y-1">
                  <p className="text-[10px] text-muted-foreground truncate" title={q.label}>Q{q.quarter}: {q.label}</p>
                  <Input
                    type="number"
                    step="0.5"
                    value={q.cost_percent}
                    onChange={(e) => updateCostPercent(i, e.target.value)}
                    className="rounded-sm h-7 text-xs text-center"
                    data-testid={`cost-q${q.quarter}-input`}
                  />
                  <p className="text-[10px] text-center text-muted-foreground">{q.cost_percent}%</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" className="h-7 text-xs" type="button" onClick={addQuarter} data-testid="add-quarter-btn">
                <Plus className="h-3 w-3 mr-1" /> Add Quarter
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs" type="button" onClick={removeLastQuarter} disabled={costQuarters.length <= 2} data-testid="remove-quarter-btn">
                <Trash2 className="h-3 w-3 mr-1" /> Remove Last
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Quarter-wise Sales Plan */}
      <Card className="rounded-sm shadow-sm" data-testid="sales-plan-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-heading tracking-tight">Quarter-wise Sales Plan</CardTitle>
              <CardDescription>
                Q1 = Down Payment (100% collected). Q2+ = RERA stage-wise collection.
                Total sold: <span className={salesTotal <= 100.5 ? "text-green-700 font-semibold" : "text-destructive font-semibold"}>{salesTotal.toFixed(1)}%</span>
                {salesTotal > 100.5 && " (exceeds 100%)"}
              </CardDescription>
            </div>
            {Math.abs(salesTotal - 100) >= 0.5 && salesTotal > 0 && (
              <Button variant="outline" size="sm" className="h-7 text-xs" type="button" onClick={normalizeSales} data-testid="normalize-sales-btn">
                Normalize to 100%
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {salesQuarters.map((sq, i) => {
              const mktRate = Math.round(plData.saleRatePerSqft || 0);
              const isDiscounted = sq.sale_rate < mktRate;
              const isQ1 = i === 0;
              return (
                <div key={`sales-q${sq.quarter}`} className={`p-2 border rounded-sm space-y-1.5 ${isQ1 ? "border-green-400 bg-green-50/30" : isDiscounted ? "border-amber-300 bg-amber-50/30" : ""}`}>
                  <p className="text-[10px] font-medium text-muted-foreground">
                    Q{sq.quarter}: {costQuarters[i]?.label || `Quarter ${sq.quarter}`}
                    {isQ1 && <span className="text-green-700 font-semibold ml-1">(Down Payment)</span>}
                    {!isQ1 && <span className="text-blue-600 ml-1">(RERA)</span>}
                  </p>
                  <div className="grid grid-cols-2 gap-1.5">
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Sale %</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={sq.sale_percent}
                        onChange={(e) => updateSalePercent(i, e.target.value)}
                        className="rounded-sm h-7 text-xs text-center"
                        data-testid={`sale-q${sq.quarter}-input`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Rate ₹/sft</Label>
                      <Input
                        type="number"
                        step="100"
                        value={sq.sale_rate}
                        onChange={(e) => updateSaleRate(i, e.target.value)}
                        className={`rounded-sm h-7 text-xs text-center ${isDiscounted ? "text-amber-700 font-semibold" : ""}`}
                        data-testid={`rate-q${sq.quarter}-input`}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* RERA Collection Stages (editable) */}
      <Card className="rounded-sm shadow-sm" data-testid="rera-stages-card">
        <CardHeader className="cursor-pointer" onClick={() => setShowReraEditor(!showReraEditor)}>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-heading tracking-tight">RERA Collection Stages</CardTitle>
              <CardDescription>
                Payment milestones for Q2+ sales.
                Total: <span className={Math.abs(reraTotal - 100) < 0.5 ? "text-green-700 font-semibold" : "text-destructive font-semibold"}>{reraTotal.toFixed(1)}%</span>
                {" | "}{reraStages.length} stages
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {Math.abs(reraTotal - 100) >= 0.5 && (
                <Button variant="outline" size="sm" className="h-7 text-xs" type="button" onClick={(e) => { e.stopPropagation(); normalizeRera(); }} data-testid="normalize-rera-btn">
                  Normalize to 100%
                </Button>
              )}
              {showReraEditor ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
          </div>
        </CardHeader>
        {showReraEditor && (
          <CardContent className="space-y-3">
            <div className="border rounded-sm overflow-hidden">
              <div className="grid grid-cols-12 gap-0 bg-secondary/50 text-xs font-medium p-2">
                <div className="col-span-5">Stage Name</div>
                <div className="col-span-3 text-center">Collection %</div>
                <div className="col-span-3 text-center">At Quarter</div>
                <div className="col-span-1"></div>
              </div>
              {reraStages.map((stage, i) => (
                <div key={`rera-${stage.name || i}-${stage.quarter}`} className="grid grid-cols-12 gap-0 items-center p-1 border-t text-sm">
                  <div className="col-span-5 px-1">
                    <Input
                      value={stage.name}
                      onChange={(e) => updateReraStage(i, "name", e.target.value)}
                      className="rounded-sm h-7 text-xs"
                      data-testid={`rera-name-${i}`}
                    />
                  </div>
                  <div className="col-span-3 px-1">
                    <Input
                      type="number"
                      step="1"
                      value={stage.percent}
                      onChange={(e) => updateReraStage(i, "percent", e.target.value)}
                      className="rounded-sm h-7 text-xs text-center"
                      data-testid={`rera-percent-${i}`}
                    />
                  </div>
                  <div className="col-span-3 px-1">
                    <Input
                      type="number"
                      min={1}
                      max={costQuarters.length}
                      value={stage.quarter}
                      onChange={(e) => updateReraStage(i, "quarter", e.target.value)}
                      className="rounded-sm h-7 text-xs text-center"
                      data-testid={`rera-quarter-${i}`}
                    />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" type="button" onClick={() => removeReraStage(i)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="h-7 text-xs" type="button" onClick={addReraStage} data-testid="add-rera-stage-btn">
              <Plus className="h-3 w-3 mr-1" /> Add Stage
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Cash Flow Results Table */}
      {analysis && analysis.rows.length > 0 && (
        <>
          {/* Cost Head Summary with actual values */}
          <Card className="rounded-sm shadow-sm border-amber-200 bg-amber-50/20" data-testid="cost-distribution-rules">
            <CardHeader className="cursor-pointer py-3" onClick={() => setShowCostRules(!showCostRules)}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading tracking-tight text-amber-800">Cost Head Distribution Summary</CardTitle>
                {showCostRules ? <ChevronUp className="h-4 w-4 text-amber-600" /> : <ChevronDown className="h-4 w-4 text-amber-600" />}
              </div>
            </CardHeader>
            {showCostRules && (
              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border">
                    <thead>
                      <tr className="bg-amber-100/50 border-b">
                        <th className="text-left p-2">Cost Head</th>
                        <th className="text-right p-2">Total Amount</th>
                        <th className="text-left p-2">Distribution Rule</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b"><td className="p-2">Rent</td><td className="p-2 text-right font-mono">{formatINR(analysis.costHeads.rent)}</td><td className="p-2">Evenly across all quarters</td></tr>
                      <tr className="border-b bg-blue-50/50"><td className="p-2 font-semibold">Initial Investment</td><td className="p-2 text-right font-mono font-semibold">{formatINR(analysis.costHeads.initialInvestment)}</td><td className="p-2">100% in Q1 (TDR, FSI, DA, BG, Deposit, etc.)</td></tr>
                      <tr className="border-b"><td className="p-2">Construction</td><td className="p-2 text-right font-mono">{formatINR(analysis.costHeads.construction)}</td><td className="p-2">As per Template %</td></tr>
                      <tr className="border-b"><td className="p-2">Other / Interest / PMC</td><td className="p-2 text-right font-mono">{formatINR(analysis.costHeads.otherCosts + analysis.costHeads.bankInterest + analysis.costHeads.pmcCost)}</td><td className="p-2">As per Template %</td></tr>
                      <tr className="border-b"><td className="p-2">End of Project (Corpus + Betterment + 50% Shifting)</td><td className="p-2 text-right font-mono">{formatINR(analysis.costHeads.endOfProjectCosts)}</td><td className="p-2">100% Last Quarter</td></tr>
                      <tr className="border-b"><td className="p-2">Bank Interest</td><td className="p-2 text-right font-mono">{formatINR(analysis.costHeads.bankInterest)}</td><td className="p-2">As per Template %</td></tr>
                      <tr className="border-b"><td className="p-2">PMC</td><td className="p-2 text-right font-mono">{formatINR(analysis.costHeads.pmcCost)}</td><td className="p-2">As per Template %</td></tr>
                      <tr className="bg-amber-100/50 font-semibold"><td className="p-2">Total</td><td className="p-2 text-right font-mono">{formatINR(analysis.totalExpenses)}</td><td className="p-2"></td></tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>

          <Card className="rounded-sm shadow-sm" data-testid="cashflow-results-table">
            <CardHeader>
              <CardTitle className="text-base font-heading tracking-tight">Quarter-wise Cash Flow</CardTitle>
              <CardDescription>Q1 collections = 100% of sales. Q2+ = RERA stage-wise collection. Hover outflow for breakdown.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cashflow-detail-table">
                  <thead>
                    <tr className="border-b bg-secondary/30">
                      <th className="text-left p-2 font-medium">Q#</th>
                      <th className="text-left p-2 font-medium">Phase</th>
                      <th className="text-right p-2 font-medium">Cost %</th>
                      <th className="text-right p-2 font-medium">Sale %</th>
                      <th className="text-right p-2 font-medium">Rate/sft</th>
                      <th className="text-right p-2 font-medium">Sale Value</th>
                      <th className="text-right p-2 font-medium">Collection</th>
                      <th className="text-right p-2 font-medium">Outflow</th>
                      <th className="text-right p-2 font-medium">Cum. In</th>
                      <th className="text-right p-2 font-medium">Cum. Out</th>
                      <th className="text-right p-2 font-medium">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.rows.map((r) => {
                      const bd = r.breakdown;
                      const breakdownLines = [
                        bd.initialInvestment > 0 && `Initial Investment: ${formatINR(bd.initialInvestment)}`,
                        bd.construction > 0 && `Construction: ${formatINR(bd.construction)}`,
                        bd.otherProportional > 0 && `Other/Interest/PMC: ${formatINR(bd.otherProportional)}`,
                        bd.rent > 0 && `Rent: ${formatINR(bd.rent)}`,
                        bd.endOfProject > 0 && `End Costs (Corpus/Betterment/Shifting): ${formatINR(bd.endOfProject)}`,
                      ].filter(Boolean).join('\n');

                      return (
                        <tr key={r.quarter} className={`border-b hover:bg-secondary/20 ${r.isDownPayment ? "bg-green-50/30" : ""}`}>
                          <td className="p-2 text-xs font-mono">
                            Q{r.quarter}
                            {r.isDownPayment && <span className="text-green-600 text-[9px] ml-0.5">DP</span>}
                          </td>
                          <td className="p-2 text-xs">{r.label}</td>
                          <td className="p-2 text-right font-mono text-xs">{r.costPercent.toFixed(1)}%</td>
                          <td className="p-2 text-right font-mono text-xs">{r.salePercent.toFixed(1)}%</td>
                          <td className="p-2 text-right font-mono text-xs">
                            {r.salePercent > 0 ? `₹${Math.round(r.rate).toLocaleString("en-IN")}` : "—"}
                          </td>
                          <td className="p-2 text-right font-mono text-xs text-muted-foreground">
                            {r.saleRevenue > 0 ? formatINR(r.saleRevenue) : "—"}
                          </td>
                          <td className="p-2 text-right font-mono text-xs text-green-700 font-semibold">{r.inflow > 0 ? formatINR(r.inflow) : "—"}</td>
                          <td className="p-2 text-right font-mono text-xs text-destructive cursor-help" title={breakdownLines}>{formatINR(r.outflow)}</td>
                          <td className="p-2 text-right font-mono text-xs">{formatINR(r.cumInflow)}</td>
                          <td className="p-2 text-right font-mono text-xs">{formatINR(r.cumOutflow)}</td>
                          <td className={`p-2 text-right font-mono text-xs font-semibold ${r.netPosition >= 0 ? "text-green-700" : "text-destructive"}`}>
                            {formatINR(r.netPosition)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-secondary/40 font-semibold">
                      <td className="p-2" colSpan={2}>Total</td>
                      <td className="p-2 text-right font-mono text-xs">{analysis.costTotal.toFixed(1)}%</td>
                      <td className="p-2 text-right font-mono text-xs">{analysis.salesTotal.toFixed(1)}%</td>
                      <td className="p-2" colSpan={2}></td>
                      <td className="p-2 text-right font-mono text-xs text-green-700">{formatINR(analysis.totalAltRevenue)}</td>
                      <td className="p-2 text-right font-mono text-xs text-destructive">{formatINR(analysis.totalExpenses)}</td>
                      <td className="p-2" colSpan={2}></td>
                      <td className={`p-2 text-right font-mono text-xs ${analysis.altProfit >= 0 ? "text-green-700" : "text-destructive"}`}>
                        {formatINR(analysis.altProfit)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Investment Recovery + Profit Comparison */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Investment Recovery */}
            <Card className="rounded-sm shadow-sm border-accent/50" data-testid="investment-recovery">
              <CardHeader>
                <CardTitle className="text-base font-heading tracking-tight text-accent">Investment Recovery</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between p-2 bg-secondary/50 rounded-sm text-sm">
                  <span className="text-muted-foreground">Initial Investment</span>
                  <span className="font-mono font-bold">{formatINR(analysis.initialInvestment)}</span>
                </div>
                <div className="flex justify-between p-2 bg-secondary/50 rounded-sm text-sm">
                  <span className="text-muted-foreground">Total Project Cost</span>
                  <span className="font-mono font-bold">{formatINR(analysis.totalExpenses)}</span>
                </div>

                {/* Key milestone recovery */}
                {analysis.rows.filter((_, i) => {
                  // Show every 3rd quarter + last quarter
                  return i % 3 === 2 || i === analysis.rows.length - 1;
                }).map(r => {
                  const recoveryPct = analysis.totalExpenses > 0 ? (r.cumInflow / analysis.totalExpenses) * 100 : 0;
                  return (
                    <div key={r.quarter} className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span>Q{r.quarter}: {r.label}</span>
                        <span className={`font-mono font-semibold ${recoveryPct >= 75 ? "text-green-700" : ""}`}>
                          {formatINR(r.cumInflow)} ({recoveryPct.toFixed(0)}% of costs)
                        </span>
                      </div>
                      <div className="w-full bg-secondary rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${recoveryPct >= 100 ? "bg-green-500" : recoveryPct >= 75 ? "bg-amber-500" : "bg-blue-500"}`}
                          style={{ width: `${Math.min(recoveryPct, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Profit Comparison */}
            <Card className="rounded-sm shadow-sm" data-testid="profit-comparison">
              <CardHeader>
                <CardTitle className="text-base font-heading tracking-tight">Profit Impact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 border rounded-sm space-y-2">
                  <p className="text-sm font-medium text-center border-b pb-1">Regular Scenario (all at market rate)</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Revenue</span>
                    <span className="font-mono">{formatINR(analysis.regularRevenue)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Expenses</span>
                    <span className="font-mono">{formatINR(analysis.totalExpenses)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="font-semibold">Profit</span>
                    <span className="font-mono font-bold text-lg">{formatINR(analysis.regularProfit)}</span>
                  </div>
                </div>

                <div className="p-3 border border-blue-200 rounded-sm space-y-2 bg-blue-50/20">
                  <p className="text-sm font-medium text-center border-b pb-1 text-blue-800">Alternative (Quarter-wise Sales)</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Revenue</span>
                    <span className="font-mono">{formatINR(analysis.totalAltRevenue)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Expenses</span>
                    <span className="font-mono">{formatINR(analysis.totalExpenses)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="font-semibold">Profit</span>
                    <span className="font-mono font-bold text-lg">{formatINR(analysis.altProfit)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-destructive">
                    <span>Revenue Sacrifice</span>
                    <span className="font-mono font-semibold">-{formatINR(analysis.revenueLoss)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Template Manager Dialog */}
      <TemplateManagerDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        templates={templates}
        onRefresh={fetchTemplates}
      />
    </div>
  );
};

export default CashFlowAnalysis;
